import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';
import type { Theme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { useCurrentUser } from '@klp/auth';
import { useAppParams } from '@klp/core/app/useAppParams';
import { Block } from '@klp/design';
import { KlpPalette } from '@klp/design/Theme/KlpPalette';

import { SakStatusSummary } from '../classes/SakStatusSummary';
import { useGetSakStatusSummaryQuery } from '../rtkApi';

const useStyles = makeStyles((theme: Theme) => ({
    box: {
        border: '1px solid transparent',
        borderColor: theme.palette.primary.medium,
        backgroundColor: KlpPalette.tertiary.light,
    },
}));

export const SakStatusSummaryBox: React.FC = () => {
    const { t } = useTranslation();
    const classes = useStyles();
    const params = useAppParams();
    const { selectedClient } = params;
    const { currentUser } = useCurrentUser();

    const kundeNummer = selectedClient?.kundeNummer;
    const userEmail = currentUser?.email;

    const sakStatusParams = useMemo(() => {
        if (kundeNummer) {
            return { kundeNummerList: [kundeNummer] };
        } else if (userEmail) {
            return { epost: userEmail };
        }
        return null;
    }, [kundeNummer, userEmail]);

    const { data: sakStatusSummaryData } = useGetSakStatusSummaryQuery(
        sakStatusParams!,
        {
            skip: !sakStatusParams,
        },
    );

    const sakStatusSummary = sakStatusSummaryData || new SakStatusSummary();

    const renderSakStatus = (
        translationKey: string,
        count: number,
        last: boolean,
    ) => (
        <span key={translationKey}>
            {t(`sak.sakStatusSummary.${translationKey}`, { count })}: {count}
            {!last && ', '}
        </span>
    );

    const numberOfStatuses = sakStatusSummary
        ? Object.keys(sakStatusSummary.statusSummary).length
        : 0;

    return (
        <Grid container={true} spacing={2}>
            <Grid item={true}>
                {sakStatusSummary && (
                    <Block
                        className={classes.box}
                        marginBottom="compact"
                        padding="tiny"
                        klpTypographyVariant="small"
                    >
                        {Object.keys(sakStatusSummary.statusSummary).map(
                            (key, index) =>
                                renderSakStatus(
                                    key,
                                    sakStatusSummary.statusSummary[key],
                                    index === numberOfStatuses - 1,
                                ),
                        )}
                    </Block>
                )}
            </Grid>
        </Grid>
    );
};
